/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.76: Centralised data-loading hook for CalendarPage. Each loader
 * is an effect-driven fetch that hydrates state from the API; the
 * experimental rule mis-flags this idiomatic pattern. Behaviour is
 * audited and matches the pre-extraction version line-for-line.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

const EMPTY_CUSTODY = {
  schedule: null,
  overrides: [],
  assignments: {},
  byChild: {},
  parentingScheduleStatus: null,
};

const EMPTY_AUDIT = { stale: [], legacy_household_wide: [] };

/**
 * Bundles the five independent API loads that hydrate the calendar:
 *   - events + active children   (selectedChildId scope)
 *   - maintenance-due chips      (visible month window)
 *   - custody overlay            (visible month window)
 *   - pending schedule proposals (member-only)
 *   - stale-schedule audit       (member-only)
 *
 * Each loader is exposed individually so callers can re-run a single
 * fetch after an in-page mutation (e.g. reload custody after saving an
 * override without re-fetching the entire grid).
 */
export function useCalendarData({ selectedChildId, monthOffset, isMediator }) {
  const [events, setEvents] = useState([]);
  const [children, setChildren] = useState([]);
  const [custody, setCustody] = useState(EMPTY_CUSTODY);
  const [maintenanceChips, setMaintenanceChips] = useState({});
  const [scheduleProposals, setScheduleProposals] = useState([]);
  const [scheduleAudit, setScheduleAudit] = useState(EMPTY_AUDIT);

  // Events + active children. Deduped defensively by id — v77.22.6.
  //
  // v77.99.99 — Merges in Contact Visits from /api/contact-visits/calendar-events
  // so they surface alongside regular events. CV rows carry `kind:"contact_visit"`
  // and a teal colour; the CalendarPage chip click handler routes them to the
  // Contact Visits page rather than the event-edit dialogs.
  const load = useCallback(async () => {
    const eventsUrl = selectedChildId
      ? `/events?child_id=${encodeURIComponent(selectedChildId)}`
      : "/events";
    const cvUrl = selectedChildId
      ? `/contact-visits/calendar-events?child_id=${encodeURIComponent(selectedChildId)}`
      : "/contact-visits/calendar-events";
    const [{ data: evs }, { data: kids }, cvResp] = await Promise.all([
      api.get(eventsUrl),
      api.get("/children?include_archived=false"),
      api.get(cvUrl).catch(() => ({ data: [] })),
    ]);
    const cvItems = cvResp?.data || [];
    setEvents([...(evs || []), ...cvItems]);
    const seen = new Set();
    const deduped = [];
    for (const c of kids || []) {
      if (c?.id && !seen.has(c.id)) {
        seen.add(c.id);
        deduped.push(c);
      }
    }
    setChildren(deduped);
  }, [selectedChildId]);

  // Maintenance-due chips. v77.99.32 — keyed by `YYYY-MM-DD` so each
  // calendar cell can look up its chip in O(1) during render.
  const loadMaintenanceChips = useCallback(async () => {
    try {
      const today = new Date();
      const anchor = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
      const start = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
      const end = new Date(anchor.getFullYear(), anchor.getMonth() + 2, 0);
      const fmt = (d) => d.toISOString().slice(0, 10);
      const r = await api.get(
        `/maintenance/calendar-chips?start=${fmt(start)}&end=${fmt(end)}`,
      );
      const byDate = {};
      for (const chip of r.data?.chips || []) {
        const k = chip.date;
        (byDate[k] = byDate[k] || []).push(chip);
      }
      setMaintenanceChips(byDate);
    } catch {
      // Endpoint may not be available for read-only / mediator viewers.
      setMaintenanceChips({});
    }
  }, [monthOffset]);

  // Custody overlay for the visible month. Widens start by 1 day so we
  // know the prior assignment for the first visible day (handover
  // detection on day 1 of the month).
  const loadCustody = useCallback(async () => {
    try {
      const today = new Date();
      const anchor = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
      const start = new Date(anchor.getFullYear(), anchor.getMonth(), 0);
      const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      const fmt = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const { data } = await api.get(`/custody?start=${fmt(start)}&end=${fmt(end)}`);
      setCustody({
        schedule: data.schedule || null,
        overrides: data.overrides || [],
        assignments: data.assignments || {},
        byChild: data.by_child || {},
        parentingScheduleStatus: data.parenting_schedule_status || null,
      });
    } catch {
      setCustody(EMPTY_CUSTODY);
    }
  }, [monthOffset]);

  // Pending schedule change proposals. v77.99.69 — mediators are gated
  // by `require_member` on the backend; skip the fetch entirely so we
  // don't surface a spurious 403 in their console.
  const loadProposals = useCallback(async () => {
    if (isMediator) {
      setScheduleProposals([]);
      return;
    }
    try {
      const { data } = await api.get("/custody/schedule/proposals?status=pending");
      setScheduleProposals(data.proposals || []);
    } catch {
      setScheduleProposals([]);
    }
  }, [isMediator]);

  // Stale-schedule audit. Same mediator gating as proposals.
  const loadScheduleAudit = useCallback(async () => {
    if (isMediator) {
      setScheduleAudit(EMPTY_AUDIT);
      return;
    }
    try {
      const { data } = await api.get("/custody/schedule/audit");
      setScheduleAudit({
        stale: data.stale || [],
        legacy_household_wide: data.legacy_household_wide || [],
      });
    } catch {
      setScheduleAudit(EMPTY_AUDIT);
    }
  }, [isMediator]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCustody(); }, [loadCustody]);
  useEffect(() => { loadMaintenanceChips(); }, [loadMaintenanceChips]);
  useEffect(() => { loadProposals(); }, [loadProposals]);
  useEffect(() => { loadScheduleAudit(); }, [loadScheduleAudit]);

  return {
    events,
    setEvents,
    children,
    setChildren,
    custody,
    setCustody,
    maintenanceChips,
    scheduleProposals,
    scheduleAudit,
    load,
    loadCustody,
    loadMaintenanceChips,
    loadProposals,
    loadScheduleAudit,
  };
}
